import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Button,
  DEFAULT_IMAGE_TWO_COLUMNS_TRANSFORMATIONS,
  GalleryGridTwoCols,
  GalleryModal,
  Picture,
  Section,
  SectionSubTitle,
  SelectionGroup,
  SelectionGroupOption,
  SelectionGroupValue,
} from 'common/components';
import { usePromoViewEvent } from 'common/hooks';
import { createElementKey } from 'utils';
import { analyticsUtils } from 'utils/analytics-utils';
import { DESIGN_STUDIO_ELEMENT_BLOCK } from '../../design-studio-constants';
import {
  IMAGE_PER_PAGE,
  DesignStudioRoomTabName,
  DESIGN_STUDIO_GALLERY_MAP,
  DESIGN_STUDIO_ROOM_LIST,
} from './design-studio-gallery-constants';
import { DesignStudioGalleryStructure } from './design-studio-gallery-types';
import {
  galleryTabs,
  galleryGrid,
  galleryImageBlock,
  galleryImage,
  galleryMoreButton,
} from './design-studio-gallery-styles';

export const DesignStudioGallery: React.FC = () => {
  const bannerRefs = useRef<(HTMLAnchorElement | HTMLButtonElement)[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [lastRefIndex, setLastRefIndex] = useState<number>(0);
  const [activeRoomTab, setActiveRoomTab] = useState(DesignStudioRoomTabName.All);
  const [imagePerPage, setImagePerPage] = useState(IMAGE_PER_PAGE);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const galleryImageList = useMemo(() => DESIGN_STUDIO_GALLERY_MAP[activeRoomTab].slice(0, imagePerPage), [
    imagePerPage,
    activeRoomTab,
  ]);

  const isShowMoreBtnVisible = useMemo(
    () => imagePerPage < DESIGN_STUDIO_GALLERY_MAP[activeRoomTab].length,
    [activeRoomTab, imagePerPage]
  );

  const promotions = useMemo(
    () =>
      galleryImageList.map((image, index: number) => ({
        name: `${DESIGN_STUDIO_ELEMENT_BLOCK}_bottom`,
        creative: image.name,
        position: (index + 1).toString(),
      })),
    [galleryImageList]
  );

  usePromoViewEvent(bannerRefs, `${DESIGN_STUDIO_ELEMENT_BLOCK} / gallery`, promotions, lastRefIndex);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setActiveImageIndex(0);
  }, [setIsModalOpen]);

  const handleRoomTabChange = (_: React.MouseEvent, value: SelectionGroupValue) => {
    setActiveRoomTab(value as DesignStudioRoomTabName);
    setImagePerPage(IMAGE_PER_PAGE);

    const activeOption: SelectionGroupOption | undefined = DESIGN_STUDIO_ROOM_LIST.find(
      (option: SelectionGroupOption) => option.value === value
    );
    if (activeOption) {
      analyticsUtils.clickButtonEvent(
        DESIGN_STUDIO_ELEMENT_BLOCK,
        activeOption.text,
        'Наши проекты интерьеров'
      );
    }
  };

  const handleShowMoreBtnClick = useCallback(() => {
    setLastRefIndex(imagePerPage);
    setImagePerPage(imagePerPage + IMAGE_PER_PAGE);
    analyticsUtils.clickButtonEvent(DESIGN_STUDIO_ELEMENT_BLOCK, 'Показать ещё', 'Наши проекты интерьеров');
  }, [imagePerPage]);

  const handleImageClick = useCallback(
    (index: number) => () => {
      setIsModalOpen(true);
      setActiveImageIndex(index);
      analyticsUtils.promoClickEvent(`${DESIGN_STUDIO_ELEMENT_BLOCK} / gallery`, promotions[index]);
    },
    [promotions]
  );

  return (
    <Section>
      <SectionSubTitle margin="m">Наши проекты интерьеров</SectionSubTitle>

      <SelectionGroup
        className={galleryTabs}
        active={activeRoomTab}
        onChange={handleRoomTabChange}
        optionList={DESIGN_STUDIO_ROOM_LIST}
        rounded
      />

      <GalleryGridTwoCols className={galleryGrid}>
        {galleryImageList.map(({ srcSmall, alt, transformations }: DesignStudioGalleryStructure, index) => (
          <button
            className={galleryImageBlock}
            type="button"
            data-position={index}
            key={createElementKey('image-', index)}
            onClick={handleImageClick(index)}
            ref={(el: HTMLButtonElement) => {
              if (el && bannerRefs) bannerRefs.current[index] = el;
            }}>
            <Picture
              className={galleryImage}
              alt={alt}
              src={srcSmall}
              title={alt}
              transformations={transformations || DEFAULT_IMAGE_TWO_COLUMNS_TRANSFORMATIONS}
              loading="lazy"
            />
          </button>
        ))}
      </GalleryGridTwoCols>

      {isShowMoreBtnVisible && (
        <Button className={galleryMoreButton} onClick={handleShowMoreBtnClick} rounded>
          Показать ещё
        </Button>
      )}

      <GalleryModal
        clickedImageIndex={activeImageIndex}
        elementBlock={DESIGN_STUDIO_ELEMENT_BLOCK}
        elementText="Наши проекты интерьеров / modal"
        galleryList={DESIGN_STUDIO_GALLERY_MAP[activeRoomTab]}
        onClose={handleCloseModal}
        show={isModalOpen}
        isShowImageName={false}
      />
    </Section>
  );
};
